import { dispatchGlobalEvent } from "@modules/util/events"

export type FloatingSidebarDetail = {
    isVisible: () => boolean
}

export const FloatingSidebarEvent = "floating-nav-visibility-changed" as const

const stickyTop: HTMLElement | null = document.getElementById("start-sticky")
const stickyBottom: HTMLElement | null = document.getElementById("end-sticky")

const sidebar: HTMLElement | null = document.getElementById("contacts-info-container")
const floatingNav: HTMLElement | null = document.querySelector(".anzeigen-list-navigation")

let stickyTopObserver: IntersectionObserver
let stickyBottomObserver: IntersectionObserver
let currentTopValue = ""

export function initStickySidebar(): void {
    window.addEventListener(
        "resize",
        (): void => {
            cleanup()
            initStickySidebar()
        },
        { once: true, passive: true },
    )

    if (!stickyTop || !stickyBottom) {
        return
    }

    const options: IntersectionObserverInit = {
        threshold: 0,
    }

    const bottomOffset = Math.floor(
        document.documentElement.clientHeight - (sidebar?.getBoundingClientRect().height || 0),
    )
    stickyTopObserver = new IntersectionObserver((entries) => evaluateStickyBoundaries(entries), options)
    stickyBottomObserver = new IntersectionObserver((entries) => evaluateStickyBoundaries(entries), {
        ...options,
        rootMargin: `0px 0px -${bottomOffset}px 0px`,
    })

    stickyTopObserver.observe(stickyTop)
    stickyBottomObserver.observe(stickyBottom)

    computeNow()
    window.addEventListener("load", () => requestAnimationFrame(computeNow), { once: true })
}

function cleanup(): void {
    stickyTopObserver.disconnect()
    stickyBottomObserver.disconnect()
}

function evaluateStickyBoundaries(entries: Array<IntersectionObserverEntry>): void {
    for (const entry of entries) {
        if (entry.target === stickyTop) {
            handleStickyTop(entry.boundingClientRect.top)
        }

        if (entry.target === stickyBottom) {
            handleStickyBottom(entry.boundingClientRect.top)
        }
    }
}

function handleStickyTop(distanceToTopEdge: number): void {
    if (distanceToTopEdge < 0) {
        currentTopValue = "0"
        sidebar && updateElementState(sidebar, ["fixed"], [], currentTopValue)
        floatingNav && updateElementState(floatingNav, ["stick-to-screen"], ["stick-to-stripe"])

        dispatchGlobalEvent("floating-nav-visibility-changed", { isVisible: () => true })
    } else {
        currentTopValue = ""
        sidebar && updateElementState(sidebar, [], ["fixed"], currentTopValue)
        floatingNav && updateElementState(floatingNav, ["stick-to-stripe"], ["stick-to-screen"])

        dispatchGlobalEvent(FloatingSidebarEvent, { isVisible: () => false })
    }
}

function handleStickyBottom(distanceToTopEdge: number): void {
    if (!sidebar || !stickyBottom) {
        return
    }

    const sidebarTopOffset: number = sidebar.getBoundingClientRect().height || 0

    if (distanceToTopEdge <= sidebarTopOffset) {
        const stickyBottomOffset: number =
            stickyBottom.getBoundingClientRect().top + (window.scrollY || document.documentElement.scrollTop)
        const sidebarHeight: number = sidebar.getBoundingClientRect().height

        updateElementState(sidebar, ["end"], [], `${stickyBottomOffset - sidebarHeight}px`)
    } else if (distanceToTopEdge > 0) {
        updateElementState(sidebar, [], ["end"], currentTopValue)
    }
}

function updateElementState(
    element: HTMLElement,
    addClass: Array<string>,
    removeClass: Array<string>,
    top: string | null = null,
): void {
    requestAnimationFrame((): void => {
        addClass.length && element.classList.add(...addClass)
        removeClass.length && element.classList.remove(...removeClass)

        if (top !== null) {
            element.style.top = top
        }
    })
}

function computeNow() {
    if (!stickyTop || !stickyBottom) return
    handleStickyTop(stickyTop.getBoundingClientRect().top)
    handleStickyBottom(stickyBottom.getBoundingClientRect().top)
}

// Register Custom Events
declare global {
    // noinspection JSUnusedGlobalSymbols
    interface DocumentEventMap {
        [FloatingSidebarEvent]: CustomEvent<FloatingSidebarDetail>
    }
    interface HTMLElementEventMap {
        [FloatingSidebarEvent]: CustomEvent<FloatingSidebarDetail>
    }
}
